import { useEffect, useMemo, useState } from 'react';

import type { AuthorityDomainExpert } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { parseExpertIdentifier } from '../utils/parse-expert-identifier';

const AGG_ENDPOINT = '/gateway/api/graphql';
const OPERATION_NAME = 'AuthoritativeAnswerExpertProfilesQuery';

const EXPERT_PROFILES_QUERY = `
query ${OPERATION_NAME}($accountIds: [ID!]!) {
  users(accountIds: $accountIds) {
    accountId
    name
    picture
    ... on AtlassianAccountUser {
      extendedProfile {
        jobTitle
      }
    }
  }
}
`;

type UserProfile = {
	accountId: string;
	name: string;
	picture: string;
	extendedProfile?: {
		jobTitle?: string;
	};
};

type AggResponse = {
	data?: {
		users?: UserProfile[];
	};
	errors?: { message: string }[];
};

export type HydratedExpert = AuthorityDomainExpert & {
	/** FE-resolved avatar URL from AGG. Undefined if hydration failed or ARI was invalid. */
	avatarUrl?: string;
	/** FE-resolved display name from AGG. Falls back to BE-provided name if hydration failed. */
	resolvedName: string;
	/** FE-resolved job title from AGG extendedProfile. Undefined if not available. */
	jobTitle?: string;
	/** The parsed accountId. Null if identifier was malformed — expert should be hidden. */
	accountId: string | null;
};

async function fetchUserProfiles(
	accountIds: string[],
	signal: AbortSignal,
): Promise<UserProfile[]> {
	const response = await fetch(`${AGG_ENDPOINT}?q=${OPERATION_NAME}`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			operationName: OPERATION_NAME,
			query: EXPERT_PROFILES_QUERY,
			variables: { accountIds },
		}),
		signal,
	});

	if (!response.ok) {
		throw new Error(`AGG request failed: ${response.status}`);
	}

	const json: AggResponse = await response.json();
	return json?.data?.users ?? [];
}

/**
 * Hydrates domain experts with FE-resolved profile data (name, avatarUrl) from AGG.
 *
 * - Experts with malformed/unresolvable identifiers are included with `accountId: null`
 *   so the caller can filter them out.
 * - Always uses FE-resolved name when available (BE name data is unreliable).
 * - Gracefully degrades — if the AGG call fails, experts retain their BE-provided name
 *   and no avatarUrl.
 */
export const useFetchUserProfiles = (
	domainExperts: AuthorityDomainExpert[],
): { experts: HydratedExpert[]; isLoading: boolean } => {
	const [profileMap, setProfileMap] = useState<Record<string, UserProfile>>({});
	const [isLoading, setIsLoading] = useState(false);

	// Parse all identifiers upfront — experts with null accountId are invalid
	const parsedExperts = useMemo(
		() =>
			domainExperts.map((expert) => ({
				...expert,
				accountId: parseExpertIdentifier(expert.identifier),
			})),
		[domainExperts],
	);

	const validAccountIds = useMemo(
		() => parsedExperts.map((e) => e.accountId).filter((id): id is string => id !== null),
		[parsedExperts],
	);

	// Stable string key to avoid re-fetching when array identity changes but content is the same
	const accountIdKey = validAccountIds.join('|');

	useEffect(() => {
		if (!validAccountIds.length) {
			return;
		}

		const controller = new AbortController();

		setIsLoading(true);
		fetchUserProfiles(validAccountIds, controller.signal)
			.then((profiles) => {
				if (controller.signal.aborted) {
					return;
				}
				const map: Record<string, UserProfile> = {};
				profiles.forEach((profile) => {
					map[profile.accountId] = profile;
				});
				setProfileMap(map);
			})
			.catch(() => {
				// Graceful degradation — retain BE-provided names, no avatars
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setIsLoading(false);
				}
			});

		return () => {
			controller.abort();
		};
		// accountIdKey is a stable derived value from validAccountIds — safe to use as dep
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountIdKey]);

	const experts = useMemo(
		() =>
			parsedExperts.map((expert) => {
				const profile = expert.accountId ? profileMap[expert.accountId] : undefined;
				return {
					...expert,
					resolvedName: profile?.name || expert.name,
					avatarUrl: profile?.picture,
					jobTitle: profile?.extendedProfile?.jobTitle,
				};
			}),
		[parsedExperts, profileMap],
	);

	return { experts, isLoading };
};
